import React, { Component, PropTypes } from 'react'
import styled from 'styled-components'
import R from 'ramda'
import Grid, { Half } from 'grid-styled'
import { translate } from 'react-i18next'
import CardContainer from '../../components/CardContainer'
import { Metrics, Fonts } from '../../Themes'
import Input from '../../components/Input'
import Button from '../../components/Button'
import { generateWebNda } from '../../sources/ContractsSource'
import { validateInput, validateForm } from '../../Utils/Validations'
import Loader from '../../components/Loader'

const CustomGrid = styled(Grid)`
  padding-left: ${Metrics.margins.medium};
  padding-right: ${Metrics.margins.medium};
  button {
    margin-top: ${Metrics.margins.base};
    float: right
  }

  @media screen and (max-width: ${Metrics.breakpoints.mobile}) {
    padding: 0;
  }
`

const CustomHalf = styled(Half)`
  margin-top: ${props => (props.noPadTop ? 0 : Metrics.margins.base)};
  padding-left: ${Metrics.margins.medium};
  padding-right: ${Metrics.margins.medium};

  @media screen and (max-width: ${Metrics.breakpoints.mobile}) {
    padding: 0;
  }
`

const CustomCardContainer = styled(CardContainer)`
  height: 100%;
`

const List = styled.ul`
  padding-left: 0;
  list-style: none;
  ${Fonts.style.Text};

  li {
    margin: ${Metrics.margins.base} 0;
    padding-bottom: ${Metrics.margins.small};
    border-bottom: ${Metrics.borders.default};

    li {
      padding-bottom: 0;
      margin: ${Metrics.margins.small} 0;
      border-bottom: none;
    }
  }
`

const Terms = styled.div`${Fonts.style.Text};`

const Title = styled.div`
  margin-bottom: ${Metrics.margins.small};
  ${Fonts.style.Label};
`

const OrderedList = styled.ol`
  padding-left: 0;
  ${Fonts.style.Text};

  counter-reset: item;
  li:before {
    content: counters(item, ".") ".";
    counter-increment: item;
  }
  ol {
    padding-left: ${Metrics.margins.medium};
  }

  li {
    display: block;
    margin: ${Metrics.margins.small} 0;
    border-bottom: none;
  }
`

class NDA extends Component {
  constructor (props) {
    super(props)
    this.state = {
      name: '',
      company: '',
      address: '',
      phoneNumber: '',
      email: '',
      generating: false,
      errors: {
        name: '',
        company: '',
        address: '',
        phone: '',
        email: ''
      }
    }
    this.handleChange = this.handleChange.bind(this)
    this.downloadFile = this.downloadFile.bind(this)
    this.handleValidate = this.handleValidate.bind(this)
  }

  handleChange (event, key) {
    const state = R.clone(this.state)
    state[key] = event.target.value
    this.setState(state)
  }

  downloadFile () {
    const { errors } = R.clone(this.state.errors)
    let validation = validateForm(this.state, errors)
    if (validation.valid) {
      this.setState({ generating: true }, () => {
        generateWebNda({
          name: this.state.name,
          company: this.state.company,
          address: this.state.address,
          phoneNumber: this.state.phoneNumber,
          email: this.state.email
        })
          .then(response => {
            this.setState({ generating: false })
            document.location = response.data.url
          })
          .catch(console.error)
      })
    } else {
      this.setState({ errors: validation.errors })
    }
  }

  handleValidate (name, value, errors) {
    let newErrors = validateInput(name, value, errors)
    this.setState({ errors: newErrors })
  }

  render () {
    const { t } = this.props
    const { errors } = this.state
    let keys = [
      { key: 'name', label: t('Name'), placeholder: t('Name') },
      { key: 'company', label: t('Company'), placeholder: t('Company Name') },
      {
        key: 'address',
        label: t('Address'),
        placeholder: t('Company Address')
      },
      { key: 'phoneNumber', label: t('Phone'), placeholder: t('Phone Number') },
      { key: 'email', label: t('E-mail'), placeholder: t('E-mail') }
    ]

    return (
      <CustomCardContainer title={t('Non Disclosure Agreement')}>
        <Loader visible={this.state.generating} />
        <CustomGrid>
          <Terms>
            <List>
              <li>
                <Title>Confidentiality and Non-Disclosure</Title>
                <OrderedList noPadTop>
                  <li>
                    The Client agrees to maintain the confidentiality of the
                    Confidential Information received by HvB and to prevent its
                    unauthorized dissemination or use, subject to the exceptions
                    enumerated in Section 5 of this Agreement.
                  </li>
                  <li>
                    The Client agrees not to use the Confidential Information
                    for purposes other than necessary to consider and enable the
                    business relationship or transaction.
                  </li>
                  <li>
                    The Client shall protect the Confidential Information by
                    using a high degree of care, to prevent the unauthorized
                    use, dissemination or publication of the Confidential
                    Information.
                  </li>
                  <li>
                    The Client shall limit its internal disclosure of the
                    Confidential Information to only those employees,
                    accountants, attorneys and affiliates who have a provable
                    need to access the information for the limited purpose of
                    the proposed business relationship or transaction.
                  </li>
                  <li>
                    This Agreement shall impose no obligations with respect to
                    Confidential Information which:
                    <OrderedList>
                      <li>
                        is disclosed with the prior written consent of HvB;
                      </li>
                      <li>
                        is now, or hereafter becomes, through no act or failure
                        to act on the part of the Client, generally known or
                        available to the public;
                      </li>
                      <li>
                        was already known by the Client before receiving such
                        information from HvB, as evidenced by written documents;
                      </li>
                      <li>
                        is information which the Client can document was
                        independently developed by the Client without reference
                        to or use of any Confidential Information received from
                        HvB;
                      </li>
                      <li>
                        is required to be disclosed pursuant to governmental
                        agencies or law. In such cases, the Client seeking to
                        disclose Confidential Information will provide notice to
                        HvB prior to such disclosure to afford HvB a reasonable
                        opportunity to file objections to the disclosure with
                        the appropriate agency or entity. In case Confidential
                        Information must legally be disclosed, the Client is
                        responsible to disclose not more than the absolute
                        minimum necessary.
                      </li>
                    </OrderedList>
                  </li>
                  <li>
                    The Client acknowledges and agrees that all the Confidential
                    Information is and shall remain the sole and exclusive
                    property of HvB, free of any and all claims of the Client.
                  </li>
                </OrderedList>
              </li>
              <li>
                <Title>Governing law and jurisdiction</Title>
                <OrderedList>
                  <li>
                    This Agreement shall in all respects be governed by and
                    construed in accordance with Swiss law, without giving
                    effect to its conflict of law principles. All disputes
                    arising from or in connection with this Agreement, including
                    disputes regarding its conclusion, validity, binding effect,
                    amendment, breach, termination or rescission, shall be
                    subject to the exclusive jurisdiction of courts of the
                    Canton of Zurich.
                  </li>
                </OrderedList>
              </li>
            </List>
          </Terms>
        </CustomGrid>

        {keys.map((el, index) => {
          return (
            <CustomHalf
              key={el.key}
              noPadLeft={index % 2 === 0}
              noPadRight={index % 2 === 1}>
              <Input
                placeholder={el.placeholder}
                name={el.key}
                required
                title={el.label}
                value={this.state[el.key]}
                handleChange={event => this.handleChange(event, el.key)}
                error={errors[el.key]}
                handleBlur={event =>
                  this.handleValidate(el.key, this.state[el.key], errors)}
              />
            </CustomHalf>
          )
        })}
        <CustomGrid>
          <Button label={t('Download')} onClick={this.downloadFile} />
        </CustomGrid>
      </CustomCardContainer>
    )
  }
}

NDA.propTypes = {
  t: PropTypes.func,
  router: PropTypes.object,
  children: PropTypes.node
}

export default translate([ 'common', 'forms', 'admin' ], { wait: true })(NDA)



// WEBPACK FOOTER //
// ./src/pages/Agreements/NDA.js